import { Form, Input, Select } from 'antd';
import Button from 'components/Button';
import { AxiosError } from 'axios';
import AlertBar from 'components/AlertBar';
import Drawer from 'components/Drawer';
import ProtectedResource from 'components/RoleBasedAccessControl/ProtectedResource';
import useAgronomistList from 'hooks/useAgronomistList';
import { API } from 'network/api';
import { useState } from 'react';
import { UserPayload } from 'types/request';
import {
  ROLE_GROUPS,
  Roles,
  ROLES,
  SUPPORTED_COUNTRIES,
} from 'utils/constants';
import { getCountryPhoneCode } from 'utils/helpers';
import { validatePhoneNumber } from 'utils/validations';

const emailRegex =
  /^[A-Za-z0-9._%+-]+@(rize\.farm|rize\.ops\.farm|rize-ops\.farm)$/;

const CreateUserForm = ({ refetchUsers }: { refetchUsers: () => void }) => {
  const [drawer, setDrawer] = useState(false);
  const [error, setError] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [validatingPhone, setValidatingPhone] = useState(false);
  const [countryCode, setCountryCode] = useState('');

  const [form] = Form.useForm();

  const country = Form.useWatch('country', form);
  const role = Form.useWatch('role', form);

  const { agronomistFilterOptions } = useAgronomistList(
    form.getFieldValue('country'),
    {
      role: 'sr',
      labelKey: 'email',
      valueKey: 'email',
    },
  );

  const onRegisterUser = async (values: UserPayload) => {
    setLoading(true);
    setError('');
    setIsSubmitted(false);

    const phoneNumber = values.phoneNumber.replace(countryCode, '').trim();
    const fullPhoneNumber = `${countryCode}${phoneNumber}`;

    try {
      const payload = {
        ...values,
        phoneNumber: fullPhoneNumber,
        reportingToUserEmailList:
          ROLE_GROUPS.JR_AGS.includes(values.role as Roles) &&
          values?.reportingToUserEmailList?.every((val) => !!val)
            ? values.reportingToUserEmailList
            : undefined,
      };
      const response = await API.post('/user/register', payload);
      if (response.status === 200) {
        setIsSubmitted(true);
        refetchUsers();
      } else {
        setError('User registration failed due to some error. Please retry!');
      }
    } catch (error) {
      if (error instanceof AxiosError) {
        setError(error?.message);
      }
    } finally {
      resetFormFields();
      setLoading(false);
    }
  };

  const resetFormFields = () => {
    form.resetFields();
  };

  const toggleDrawer = () => {
    resetFormFields();
    setDrawer(!drawer);
  };

  const handleCountryChange = (value: string) => {
    const countryPhoneCode = getCountryPhoneCode(value) ?? '';
    setCountryCode(countryPhoneCode);
  };

  const disableSubmitButtom = () => {
    return (
      loading ||
      validatingPhone ||
      form.getFieldsError().some(({ errors }) => errors.length > 0)
    );
  };

  const drawerContent = (
    <div>
      {!isSubmitted ? (
        <Form
          form={form}
          layout="vertical"
          initialValues={{
            remember: false,
          }}
          onFinish={onRegisterUser}
        >
          <Form.Item
            label="Name"
            name="name"
            rules={[
              {
                required: true,
                message: 'Please input your username!',
              },
            ]}
          >
            <Input />
          </Form.Item>

          <Form.Item
            label="Email"
            name="email"
            validateTrigger="onBlur"
            rules={[
              {
                pattern: emailRegex,
                message: 'Invalid email address!',
              },
              {
                required: true,
                message: 'Please input your email!',
              },
            ]}
          >
            <Input />
          </Form.Item>

          <Form.Item
            label="Password"
            name="password"
            rules={[
              {
                required: true,
                message: 'Please input your password!',
              },
            ]}
          >
            <Input.Password />
          </Form.Item>

          <Form.Item
            label="Country"
            name="country"
            rules={[
              {
                required: true,
                message: 'Please select a country!',
              },
            ]}
          >
            <Select
              options={SUPPORTED_COUNTRIES}
              placeholder="Select Country"
              style={{
                maxWidth: 500,
              }}
              onChange={handleCountryChange}
            />
          </Form.Item>

          <Form.Item
            label="Phone"
            name="phoneNumber"
            hasFeedback
            rules={[
              {
                required: true,
                message: 'Phone number is required.',
              },
              {
                pattern: /^[0-9]*$/,
                message: 'Only Numbers are allowed',
              },
              {
                validator: (_, value) =>
                  validatePhoneNumber(value, countryCode, setValidatingPhone),
              },
              {
                validator: (_, value) => {
                  if (form.getFieldValue('country') === 'Test') {
                    return Promise.resolve();
                  }
                  if (
                    !value ||
                    value.trim() === '' ||
                    value === countryCode ||
                    value.replace(countryCode, '').trim().length === 0
                  ) {
                    return Promise.reject('Please enter a valid phone number.');
                  }
                  return Promise.resolve();
                },
              },
            ]}
            validateDebounce={300}
            normalize={(value, prevValue) =>
              /^[0-9]*$/.test(value) || value === '' ? value : (prevValue ?? '')
            }
          >
            <Input
              prefix={`+${getCountryPhoneCode(country)}`}
              type="tel"
              disabled={!country}
            />
          </Form.Item>

          <Form.Item
            label="Role"
            name="role"
            rules={[
              {
                required: true,
                message: 'Please select your role!',
              },
            ]}
          >
            <Select
              options={ROLES}
              placeholder="Select Role"
              style={{
                maxWidth: 500,
              }}
            />
          </Form.Item>

          {ROLE_GROUPS.JR_AGS.includes(role) && (
            <Form.Item
              label="Reporting to"
              name={['reportingToUserEmailList', 0]}
              labelCol={{ span: 24 }}
            >
              <Select
                options={agronomistFilterOptions}
                placeholder="Select direct report of Junior Agronomist"
                style={{
                  maxWidth: 500,
                }}
              />
            </Form.Item>
          )}

          <Form.Item>
            <Button
              className="w-100"
              type="primary"
              htmlType="submit"
              disabled={disableSubmitButtom()}
            >
              Register User
            </Button>
          </Form.Item>
        </Form>
      ) : (
        <div>
          <h1>Success!</h1>
          <p>The user has been registered successfully</p>
          <Button onClick={() => setIsSubmitted(false)} type="primary">
            Register new user
          </Button>
        </div>
      )}
    </div>
  );

  return (
    <>
      {error && <AlertBar message={error} type="error" clearAfter={20000} />}
      <div className="d-flex justify-content-between align-items-center pb-2 mb-2">
        <div className="fs-3 fw-semibold">User Details</div>
        <ProtectedResource permission="user:add">
          <Button type="primary" onClick={toggleDrawer}>
            Register New User
          </Button>
        </ProtectedResource>
      </div>
      <Drawer
        title={'Register New User'}
        open={drawer}
        onClose={toggleDrawer}
        content={drawerContent}
      />
    </>
  );
};

export default CreateUserForm;
